export type TalentReviewGridIdentity = {
	templateID: string;
	groupID: string;
	localDate: string;
};

/** One in-flight save: which user it changes, in which grid, in which order. */
export type Save = {
	sequence: number;
	userID: string;
	identity: TalentReviewGridIdentity;
};

export type GridSlice<
	P extends { user: { id: string } } = { user: { id: string } },
	A = unknown,
> = {
	localDate: string;
	activity: A;
	placements: readonly P[];
};

export type PersistKind = 'local' | 'skip' | 'remove' | 'update' | 'missing';

export type Rollback<P> = { placement?: P; saves: number };

// Replaces one user's placement, or drops it when there is no placement left.
// Placements of other users stay untouched.
export const mergePlacement = <T extends { user: { id: string } }>(
	current: readonly T[],
	placement: T | undefined,
	userID: string,
) => {
	const rest = current.filter((item) => item.user.id !== userID);
	return placement ? rest.concat(placement) : rest;
};

/**
 * Which persist path to take. Local corrections never hit the server. A stale
 * grid (the loaded identity no longer matches the UI) is a no-op. A remove
 * has no placement body; an update needs the moved person still in `next`.
 * @param options which persist path to consider
 * @param options.local a local correction that never hits the server
 * @param options.stale the loaded grid no longer matches the UI
 * @param options.remove the person is being taken off the grid
 * @param options.hasPlacement the moved person is still in `next`
 */
export const persistKind = (options: {
	local?: boolean;
	stale: boolean;
	remove?: boolean;
	hasPlacement: boolean;
}): PersistKind => {
	if (options.local) {
		return 'local';
	}

	if (options.stale) {
		return 'skip';
	}

	if (options.remove) {
		return 'remove';
	}

	return options.hasPlacement ? 'update' : 'missing';
};

/**
 * Builds the TalentReviewGrid value to write to the Apollo cache after a
 * per-user save. Only this save's user is taken from the response; other
 * placements stay as cached, so an out-of-order response cannot undo a
 * concurrent save. Activity is grid-wide: a newer response wins, an older one
 * keeps the activity already written. Mutates `appliedSaves` and
 * `appliedActivity` when the write goes through.
 * @param save the in-flight save being applied
 * @param appliedSaves the latest applied sequence per user
 * @param appliedActivity the latest applied activity sequence
 * @param appliedActivity.current that sequence
 * @param cached the grid currently in the Apollo cache
 * @param next the mutation response
 */
export const nextCachedGrid = <G extends GridSlice>(
	save: Save,
	appliedSaves: Map<string, number>,
	appliedActivity: { current: number },
	cached: G | undefined,
	next: G | undefined,
): G | undefined => {
	if (!next || save.sequence < (appliedSaves.get(save.userID) ?? 0)) {
		return undefined;
	}

	appliedSaves.set(save.userID, save.sequence);

	const activity =
		cached && save.sequence < appliedActivity.current ? cached.activity : next.activity;
	appliedActivity.current = Math.max(appliedActivity.current, save.sequence);

	if (!cached) {
		return next;
	}

	return {
		...cached,
		activity,
		placements: mergePlacement(
			cached.placements,
			next.placements.find((item) => item.user.id === save.userID),
			save.userID,
		),
	} as G;
};

/**
 * Builds the TalentReviewGrid value after a placement-help questionnaire.
 * Nothing on the grid moves: cached placements stay, activity comes from the
 * response unless a newer write already landed.
 * @param sequence the questionnaire response's sequence
 * @param appliedActivity the latest applied activity sequence
 * @param appliedActivity.current that sequence
 * @param cached the grid currently in the Apollo cache
 * @param next the mutation response
 */
export const nextPlacementHelpGrid = <G extends GridSlice>(
	sequence: number,
	appliedActivity: { current: number },
	cached: G | undefined,
	next: G | undefined,
): G | undefined => {
	if (!next) {
		return undefined;
	}

	const activity = cached && sequence < appliedActivity.current ? cached.activity : next.activity;
	appliedActivity.current = Math.max(appliedActivity.current, sequence);

	return cached ? ({ ...next, activity, placements: cached.placements } as G) : next;
};

/**
 * Starts (or continues) this user's rollback: the placement to restore when a
 * save fails. The first in-flight save snapshots the current placement; later
 * overlapping saves reuse that entry and bump `saves`.
 * @param rollbacks in-flight rollbacks by user
 * @param userID whose save is starting
 * @param current their placement before this save
 */
export const takeRollback = <P>(
	rollbacks: Map<string, Rollback<P>>,
	userID: string,
	current?: P,
): Rollback<P> => {
	const rollback = rollbacks.get(userID) ?? { placement: current, saves: 0 };
	rollback.saves += 1;
	rollbacks.set(userID, rollback);
	return rollback;
};

/**
 * A successful save settles rollback on the server's placement, so a later
 * failure of an older overlapping save cannot revert past it.
 * @param rollback the in-flight rollback to settle
 * @param placements the server's placements
 * @param userID whose save succeeded
 */
export const settleRollback = <P extends { user: { id: string } }>(
	rollback: Rollback<P>,
	placements: readonly P[] | undefined,
	userID: string,
) => {
	rollback.placement = placements?.find((item) => item.user.id === userID);
};

/**
 * Ends one in-flight save for this user. Drops the rollback entry when nothing
 * is left in flight, so a later save snapshots a fresh placement.
 * @param rollbacks in-flight rollbacks by user
 * @param userID whose save is ending
 * @param rollback the in-flight rollback to release
 */
export const releaseRollback = <P>(
	rollbacks: Map<string, Rollback<P>>,
	userID: string,
	rollback: Rollback<P>,
) => {
	rollback.saves -= 1;
	if (rollback.saves === 0) {
		rollbacks.delete(userID);
	}
};

/**
 * Whether the mutation response should overwrite optimistic state for this
 * save. A newer start for the same user wins, so an older response is ignored.
 * When it does apply, `placement` is missing if the person was removed.
 * @param save the in-flight save being applied
 * @param startedSaves the latest started sequence per user
 * @param next the mutation response
 */
export const applyServerPlacement = <P extends { user: { id: string } }>(
	save: Save,
	startedSaves: Map<string, number>,
	next: { placements: readonly P[] } | undefined,
): { apply: false } | { apply: true; placement: P | undefined } => {
	if (!next || startedSaves.get(save.userID) !== save.sequence) {
		return { apply: false };
	}

	return {
		apply: true,
		placement: next.placements.find((item) => item.user.id === save.userID),
	};
};

/**
 * The response grid can miss a rating that was just sent: the read ran before
 * the write was visible, or a follow-up write did not carry it. Keep the
 * rating that was sent for this user. A clear is not filled back in.
 * @param next the mutation response grid
 * @param userID whose rating was saved
 * @param sent the rating that was sent, if any
 */
export const keepSentRetentionRisk = <
	P extends { user: { id: string }; retentionRisk?: string | null },
	G extends GridSlice<P>,
>(
	next: G | undefined,
	userID: string,
	sent: string | null | undefined,
): G | undefined => {
	if (!next || !sent) {
		return next;
	}

	let changed = false;
	const placements = next.placements.map((item) => {
		if (item.user.id !== userID || item.retentionRisk) {
			return item;
		}

		changed = true;
		return { ...item, retentionRisk: sent };
	});

	return changed ? ({ ...next, placements } as G) : next;
};

/**
 * A motivation save carries the text as it was when the debounced save fired.
 * When more has been typed since, the response is older than what is on
 * screen: applying it as-is would put the old text back under the cursor. The
 * local text is kept in that case; the rest of the placement (createdAt,
 * previous, actor) still comes from the server.
 * @param next the mutation response grid
 * @param userID whose motivation was saved
 * @param saved the motivation that was sent
 * @param latest the motivation typed most recently
 */
export const keepLatestMotivation = <
	P extends { user: { id: string }; motivation?: string | null },
	G extends GridSlice<P>,
>(
	next: G | undefined,
	userID: string,
	saved: string | null | undefined,
	latest: string | null | undefined,
): G | undefined => {
	if (!next || (saved ?? '') === (latest ?? '')) {
		return next;
	}

	return {
		...next,
		placements: next.placements.map((item) =>
			item.user.id === userID ? { ...item, motivation: latest } : item,
		),
	};
};
